/*
 * Deskon - rebuilds the contents of the mobile navigation drawer.
 *
 * WHY CLIENT-SIDE. snippets/header-drawer.liquid is 53 KB and sections/header.liquid 42 KB. The
 * Theme API writes whole files and truncates past roughly 15 KB, so neither can be edited. The
 * drawer's SHELL is left exactly as the theme built it - the <details>, its classes, its
 * open/close animation - and only its contents are replaced.
 *
 * ---------------------------------------------------------------------------------------------
 * FINDING THE PANEL - the first attempt failed here, and it is worth writing down why
 * ---------------------------------------------------------------------------------------------
 * The first version took "the last child of the <details> that is not the <summary>". That looked
 * safe and was not. header-drawer.js - the theme's own code - does:
 *
 *     details.querySelector('.menu-drawer, .menu-drawer__submenu')
 *
 * Two class names, which means the drawer keeps SIBLING panels: the main one plus a sliding panel
 * per submenu. The last child is therefore a hidden submenu, not the drawer. The script emptied
 * that, silently and invisibly, and the real drawer rendered untouched - a failure with no error
 * and no visual clue except that nothing happened.
 *
 * The panel is now found BY THE CLASS THE THEME'S OWN JS LOOKS FOR, with the submenus excluded.
 * That class is not a guess: it is read out of header-drawer.js. Position is kept only as a last
 * resort, and even then it takes the FIRST non-summary child rather than the last.
 *
 * KEEPING THE PANEL ELEMENT ITSELF IS LOAD-BEARING. header-drawer.js waits on THAT element's
 * animationend before trapping focus and before resetting on close. Replacing the node - rather
 * than emptying it - would strip the class the theme looks for, onAnimationEnd would never fire,
 * and the drawer would open without focus trapping and fail to reset on close.
 *
 * IT SAYS SO WHEN IT FAILS. Every exit path logs one line beginning [deskon-nav]. The first
 * version was silent, which is why a wrong element could be emptied for a whole round trip
 * without anyone being able to tell whether the script had even run.
 *
 * THE MENU COMES FROM LIQUID, not from the markup being replaced - see
 * snippets/deskon-nav-drawer-data.liquid. Scraping the old DOM would have meant guessing which of
 * Horizon's two submenu renderings was in use, which is the same trap as above.
 *
 * THE DATE IS COMPUTED ON EVERY PAGE LOAD, never stored: today + 2 days, day then month, month
 * capitalised (UK style, "18 October" - 16/10/2026), no year. The whole "until ..." string is
 * one node with white-space: nowrap.
 *
 * IT RE-APPLIES. The header is a Shopify section, so the editor re-renders it on every setting
 * change. The observer is scoped to #header-group to keep it cheap, and the work is idempotent -
 * the guard is the presence of .dk-nav inside the panel.
 */

(function () {
  'use strict';

  var TAG = '[deskon-nav]';
  var OFFER_DAYS_AHEAD = 2;
  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  var CLOSE_SVG =
    '<svg viewBox="0 0 17 17" width="17" height="17" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M1.4 1.4 15.6 15.6M15.6 1.4 1.4 15.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';

  var ARROW_SVG =
    '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M2.2 8h11.6M9.4 3.8 13.8 8l-4.4 4.2" stroke="currentColor" stroke-width="1.2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var lastReason = 'not run yet';

  function offerDate() {
    var d = new Date();
    d.setDate(d.getDate() + OFFER_DAYS_AHEAD);
    return d.getDate() + ' ' + MONTHS[d.getMonth()];
  }

  function svgNode(markup) {
    var t = document.createElement('template');
    t.innerHTML = markup;
    return t.content.firstElementChild;
  }

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function readData() {
    var tag = document.getElementById('deskon-nav-data');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent);
    } catch (error) {
      console.warn(TAG, 'menu data is not valid JSON', error);
      return null;
    }
  }

  function host() {
    return document.querySelector('header-drawer');
  }

  /*
   * 1. .menu-drawer, submenus excluded - the class header-drawer.js itself queries.
   * 2. failing that, the FIRST non-summary child of the details.
   * Never the last: that is a submenu panel, which is what broke the first attempt.
   */
  function findPanel() {
    var drawer = host();
    var scope = drawer || document;

    var panel = scope.querySelector('.menu-drawer:not(.menu-drawer__submenu)');
    if (panel) return panel;

    panel = scope.querySelector('.menu-drawer');
    if (panel && !panel.classList.contains('menu-drawer__submenu')) return panel;

    var details = scope.querySelector('details');
    if (!details) return null;

    for (var i = 0; i < details.children.length; i += 1) {
      var child = details.children[i];
      if (child.tagName === 'SUMMARY') continue;
      if (child.classList.contains('menu-drawer__submenu')) continue;
      return child;
    }
    return null;
  }

  function logoImage(data) {
    if (data.logo) {
      var img = el('img');
      img.src = data.logo;
      img.alt = '';
      return img;
    }
    var source = document.querySelector('#header-group img');
    if (!source) return null;
    var clone = source.cloneNode(true);
    clone.removeAttribute('class');
    clone.removeAttribute('width');
    clone.removeAttribute('height');
    clone.removeAttribute('style');
    clone.alt = '';
    return clone;
  }

  function closeDrawer() {
    var drawer = host();
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
      return;
    }
    var details = (drawer || document).querySelector('details');
    if (!details) return;
    details.classList.remove('menu-open');
    details.removeAttribute('open');
    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  function buildHead(data) {
    var head = el('div', 'dk-nav__head');

    var logo = el('a', 'dk-nav__logo');
    logo.href = data.home || '/';
    var img = logoImage(data);
    if (img) logo.appendChild(img);
    head.appendChild(logo);

    var close = el('button', 'dk-nav__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close menu');
    close.appendChild(svgNode(CLOSE_SVG));
    close.addEventListener('click', closeDrawer);
    head.appendChild(close);

    return head;
  }

  function buildCategories(items) {
    var nav = el('nav', 'dk-nav__cats');
    items.forEach(function (item) {
      var row = el('a', 'dk-nav__cat');
      row.href = item.url;
      var label = el('span', 'dk-nav__cat-label');
      label.textContent = item.title;
      row.appendChild(label);
      row.appendChild(svgNode(ARROW_SVG));
      nav.appendChild(row);
      /* A separator after EVERY row, including the last - that is what the design shows. */
      nav.appendChild(el('div', 'dk-nav__sep'));
    });
    return nav;
  }

  function buildGroup(group) {
    var section = el('div', 'dk-nav__group');
    var title = el('p', 'dk-nav__group-title');
    title.textContent = group.title;
    section.appendChild(title);

    var links = el('div', 'dk-nav__group-links');
    group.children.forEach(function (child) {
      var link = el('a', 'dk-nav__group-link');
      link.href = child.url;
      link.textContent = child.title;
      links.appendChild(link);
    });
    section.appendChild(links);
    return section;
  }

  function buildFoot(data) {
    var foot = el('div', 'dk-nav__foot');

    var button = el('a', 'dk-nav__btn');
    button.href = (data.cta && data.cta.url) || '/collections/all';
    button.textContent = (data.cta && data.cta.label) || 'Discover the collection';
    foot.appendChild(button);

    var offer = el('p', 'dk-nav__offer');
    var percent = el('span', 'dk-nav__offer-pct');
    percent.textContent = '−30%';
    var until = el('span', 'dk-nav__offer-until');
    // "-30% on selected lamps" (17/10/2026, user request). The dated version was
    // 'until ' + offerDate(); offerDate() is kept for that.
    until.textContent = 'on selected lamps';
    offer.appendChild(percent);
    offer.appendChild(until);
    foot.appendChild(offer);

    return foot;
  }

  function build(data) {
    var root = el('div', 'dk-nav');
    root.appendChild(buildHead(data));

    var body = el('div', 'dk-nav__body');
    var items = Array.isArray(data.items) ? data.items : [];
    var plain = items.filter(function (i) { return !i.children || !i.children.length; });
    var groups = items.filter(function (i) { return i.children && i.children.length; });

    if (plain.length) body.appendChild(buildCategories(plain));
    groups.forEach(function (group) { body.appendChild(buildGroup(group)); });
    root.appendChild(body);

    root.appendChild(buildFoot(data));
    return root;
  }

  function apply() {
    var panel = findPanel();
    if (!panel) {
      lastReason = host() ? 'drawer found, panel not found' : 'no <header-drawer> in the document';
      return false;
    }
    if (panel.querySelector('.dk-nav')) {
      lastReason = 'already applied';
      return true;
    }

    var data = readData();
    if (!data) {
      lastReason = 'menu data missing or invalid';
      return false;
    }

    panel.classList.add('dk-nav-panel');
    panel.textContent = '';
    panel.appendChild(build(data));
    lastReason = 'applied to .' + (panel.className || panel.tagName.toLowerCase());
    return true;
  }

  var announced = false;
  function run() {
    var ok = apply();
    if (ok && !announced) {
      announced = true;
      console.log(TAG, lastReason);
    }
    return ok;
  }

  [0, 60, 200, 600, 1400].forEach(function (delay) {
    if (delay === 0) run();
    else setTimeout(run, delay);
  });

  setTimeout(function () {
    if (!announced) console.warn(TAG, 'not applied -', lastReason);
  }, 2500);

  document.addEventListener('shopify:section:load', function () { setTimeout(run, 0); });
  window.addEventListener('load', run);

  var group = document.getElementById('header-group');
  if (group && window.MutationObserver) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        apply();
      });
    }).observe(group, { childList: true, subtree: true });
  }

  /* Paste deskonNav.debug() into the console if it still will not apply. */
  window.deskonNav = {
    apply: run,
    debug: function () {
      var drawer = host();
      var details = drawer && drawer.querySelector('details');
      return {
        reason: lastReason,
        hasHeaderDrawer: !!drawer,
        hasDetails: !!details,
        detailsChildren: details
          ? Array.prototype.map.call(details.children, function (c) {
              return c.tagName.toLowerCase() + '.' + (c.className || '(no class)');
            })
          : null,
        menuDrawers: Array.prototype.map.call(
          document.querySelectorAll('.menu-drawer, .menu-drawer__submenu'),
          function (c) { return c.tagName.toLowerCase() + '.' + c.className; }
        ),
        hasData: !!document.getElementById('deskon-nav-data'),
      };
    },
  };
})();